import Placeholder from "components/Layout/Placeholder";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordLabel from "components/RecordLabel";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import { FeConfigType } from "config/config";
import { useEffect, useMemo, useState } from "react";
import { getRelativeUrl } from "utils/service";
import {
    ConnectorEgressPoliciesResult,
    ConnectorEgressPolicy,
    connectorEgressSummary,
    egressDefaultActionLabel,
    egressScopeLabel,
    fetchConnectorEgressPolicies,
} from "./connectorEgressPolicies";
import "./ConnectorEgressPolicyList.scss";

/**
 * "Egress policies" tab on a connector screen.
 *
 * Lists the egress policies that govern this connector's outbound connections,
 * resolved by the backend the same way the egress gate resolves them
 * (`GET /connector/{id}/egress-policies`): policies on the connector, its
 * bundle, its project, and the workspace-wide ones for the connector's channel.
 * Re-fetches when the connector is saved (`modifiedAt` changes), since a new
 * connector type or location changes which policies apply.
 */
export const ConnectorEgressPolicyList = ({
    connectorId,
    modifiedAt,
}: {
    connectorId: string;
    modifiedAt?: string | null;
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [result, setResult] = useState<ConnectorEgressPoliciesResult | null>(null);

    useEffect(() => {
        let isCurrent = true;
        setIsLoading(true);
        fetchConnectorEgressPolicies(connectorId)
            .then((data) => {
                if (isCurrent) {
                    setResult(data);
                }
            })
            .catch((error: any) => {
                if (isCurrent) {
                    setResult(null);
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Sorry, couldn't load the egress policies for this connector"
                            error={error}
                        />
                    );
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setIsLoading(false);
                }
            });
        return () => {
            isCurrent = false;
        };
    }, [connectorId, modifiedAt]);

    const columns: TableColumn[] = useMemo(
        () => [
            {
                label: "Name",
                key: "name",
                shown: true,
                sortable: false,
                dto: (row: ConnectorEgressPolicy) => (
                    <RecordLabel
                        link={getRelativeUrl("egress_policy" as FeConfigType, row.id)}
                        name={row.name ?? row.id}
                    />
                ),
            },
            {
                label: "Applies through",
                key: "scope_kind",
                shown: true,
                sortable: false,
                dto: (row: ConnectorEgressPolicy) => egressScopeLabel(row.scope_kind),
            },
            {
                label: "Enabled",
                key: "enabled",
                shown: true,
                sortable: false,
                dto: (row: ConnectorEgressPolicy) => (row.enabled ? "Yes" : "No"),
            },
            {
                label: "Unmatched destinations",
                key: "default_action",
                shown: true,
                sortable: false,
                dto: (row: ConnectorEgressPolicy) => egressDefaultActionLabel(row.default_action),
            },
            {
                label: "Rules",
                key: "rule_count",
                shown: true,
                sortable: false,
                dto: (row: ConnectorEgressPolicy) => String(row.rule_count),
            },
        ],
        []
    );

    if (isLoading) {
        return <Placeholder.List />;
    }
    if (!result) {
        return null;
    }
    return (
        <div className="connectorEgressPolicyList">
            <p className="connectorEgressPolicyHint">{connectorEgressSummary(result)}</p>
            {result.policies.length > 0 && (
                <RecordTable
                    columns={columns}
                    data={result.policies.map((policy) => ({
                        ...policy,
                        name: policy.name ?? undefined,
                    }))}
                    totalItemCount={result.policies.length}
                    recordTypes="all"
                    isLoading={false}
                    tableSettingsKey="connectorEgressPolicyList"
                    options={{
                        toolbar: "none",
                        toolbarFilters: false,
                        selectableRows: "none",
                        showSearch: false,
                        viewColumns: false,
                        jumpToPage: false,
                        pagination: false,
                        density: "high",
                        setRowProps: (_row: any[], dataIndex: number) =>
                            result.policies[dataIndex]?.in_effect
                                ? {}
                                : { className: "connectorEgressPolicyInactive" },
                    }}
                />
            )}
        </div>
    );
};
